import React, { useState } from "react";
import {
  Person,
  PersonCardInteraction,
  PersonViewType,
} from "@microsoft/mgt-react";
import {
  Button,
  Card,
  Checkbox,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { DateTime } from "luxon";
import style from "../RegistrationList.module.css";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import EventSeatIcon from "@mui/icons-material/EventSeat";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import EmailIcon from "@mui/icons-material/Email";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import DoNotDisturbAltIcon from "@mui/icons-material/DoNotDisturbAlt";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import CheckIcon from "@mui/icons-material/Check";
import useIsAdmin from "../../../../hooks/useIsAdmin";

const RegListCard = ({
  registration,
  isWaitlist,
  unRegister,
  leaveWaitlist,
  acceptSeat,
  declineSeat,
}) => {
  //state for if user should be notified on accept or decline offered seat
  const [notify, setNotify] = useState({
    acceptMail: true, //send email on accept
    acceptEvent: true, //add event on accept
    declineMail: true, //send email on decline
  });
  const isAdmin = useIsAdmin();

  return (
    <Card
      variant="outlined"
      className={style.listCard}
      sx={{ backgroundColor: "list.notSelected.main" }}
    >
      <Box sx={{ flex: "0 0 350px", display: "flex", alignItems: "center" }}>
        <Person
          view={PersonViewType.threelines}
          personQuery={registration.staff.mail}
          personCardInteraction={PersonCardInteraction.click}
          //avatarSize="large"
          line2Property="jobTitle"
          line3Property="department"
          //line3Property="mail"
        ></Person>
      </Box>

      {!isWaitlist ? (
        //registration list controls
        <>
          <Box className={style.cardDate}>
            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
              <HowToRegIcon fontSize="inherit" /> Registered
            </Typography>
            <Typography variant="body2">
              {DateTime.fromISO(registration.registeredDatetime).toLocaleString(
                DateTime.DATE_MED
              )}{" "}
              {DateTime.fromISO(registration.registeredDatetime).toLocaleString(
                DateTime.TIME_SIMPLE
              )}
            </Typography>
          </Box>
          {isAdmin && (
            <Box sx={{ marginLeft: "auto" }}>
              <Tooltip
                title={`Un-register ${registration.staff.legalFirstname}`}
              >
                <IconButton
                  color="warning"
                  onClick={() => {
                    unRegister(registration.staff.mail);
                  }}
                >
                  <PersonRemoveIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </>
      ) : (
        //waitlist controls
        <>
          <Box className={style.cardDate}>
            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
              <AccessTimeIcon fontSize="inherit" /> Joined waitlist
            </Typography>
            <Typography variant="body2">
              {DateTime.fromISO(
                registration.joinedWaitlistDatetime
              ).toLocaleString(DateTime.DATE_MED)}{" "}
              {DateTime.fromISO(
                registration.joinedWaitlistDatetime
              ).toLocaleString(DateTime.TIME_SIMPLE)}
            </Typography>
          </Box>
          {registration.offeredSeatDatetime && (
            <Box
              className={style.cardDate}
              sx={{ flexDirection: "row", gap: "1rem" }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  flexDirection: "column",
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  <EventSeatIcon fontSize="inherit" /> Offered seat
                </Typography>
                <Typography variant="body2">
                  {DateTime.fromISO(
                    registration.offeredSeatDatetime
                  ).toLocaleString(DateTime.DATE_MED)}{" "}
                  {DateTime.fromISO(
                    registration.offeredSeatDatetime
                  ).toLocaleString(DateTime.TIME_SIMPLE)}
                </Typography>
              </Box>
              {isAdmin &&
                registration.offeredSeatDatetime &&
                !registration.offerExpiredDatetime && (
                  <Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Box>
                        <Tooltip
                          title={`Accept offered seat on behalf of ${
                            registration.staff.legalFirstname
                          }. ${
                            notify.acceptMail
                              ? "They will receive a registration email."
                              : ""
                          }
                          ${
                            notify.acceptEvent
                              ? "An event will be added to their calendar"
                              : ""
                          }
                          `}
                        >
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            startIcon={<CheckIcon />}
                            onClick={() => {
                              acceptSeat(
                                registration.staff.mail,
                                notify.acceptMail,
                                notify.acceptEvent
                              );
                            }}
                          >
                            Accept
                          </Button>
                        </Tooltip>

                        <Tooltip
                          title={`Toggle send registration email to ${registration.staff.legalFirstname} on accept`}
                        >
                          <Checkbox
                            icon={<MailOutlineIcon />}
                            checkedIcon={<EmailIcon />}
                            onChange={() => {
                              setNotify({
                                ...notify,
                                acceptMail: !notify.acceptMail,
                              });
                            }}
                            checked={notify.acceptMail}
                          />
                        </Tooltip>
                        <Tooltip
                          title={`Toggle add to ${registration.staff.legalFirstname}'s calendar on accept`}
                        >
                          <Checkbox
                            icon={<EventBusyIcon />}
                            checkedIcon={<EventAvailableIcon />}
                            onChange={() => {
                              setNotify({
                                ...notify,
                                acceptEvent: !notify.acceptEvent,
                              });
                            }}
                            checked={notify.acceptEvent}
                          />
                        </Tooltip>
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Box>
                        <Tooltip
                          title={`Decline offered seat on behalf of ${
                            registration.staff.legalFirstname
                          }. ${
                            notify.declineMail
                              ? "They will receive an email."
                              : ""
                          }`}
                        >
                          <Button
                            variant="contained"
                            color="warning"
                            size="small"
                            startIcon={<DoNotDisturbAltIcon />}
                            onClick={() => {
                              declineSeat(
                                registration.staff.mail,
                                notify.declineMail
                              );
                            }}
                          >
                            Decline
                          </Button>
                        </Tooltip>

                        <Tooltip
                          title={`Toggle send email to ${registration.staff.legalFirstname} on decline`}
                        >
                          <Checkbox
                            icon={<MailOutlineIcon />}
                            checkedIcon={<EmailIcon />}
                            onChange={() => {
                              setNotify({
                                ...notify,
                                declineMail: !notify.declineMail,
                              });
                            }}
                            checked={notify.declineMail}
                          />
                        </Tooltip>
                      </Box>
                    </Box>
                  </Box>
                )}
            </Box>
          )}

          {registration.offerExpiredDatetime && (
            <Box className={style.cardDate}>
              <Typography
                variant="body2"
                sx={{ fontWeight: "bold" }}
                alignItems="center"
              >
                {registration.declined ? (
                  <>
                    <ThumbDownIcon fontSize="inherit" />
                    {" Declined"}
                  </>
                ) : (
                  <>
                    <EventBusyIcon fontSize="inherit" />
                    {" Expired"}
                  </>
                )}
              </Typography>
              <Typography variant="body2">
                {DateTime.fromISO(
                  registration.offerExpiredDatetime
                ).toLocaleString(DateTime.DATE_MED)}{" "}
                {DateTime.fromISO(
                  registration.offerExpiredDatetime
                ).toLocaleString(DateTime.TIME_SIMPLE)}
              </Typography>
            </Box>
          )}
          {isAdmin && (
            <Box sx={{ marginLeft: "auto" }}>
              <Tooltip
                title={`Remove ${registration.staff.legalFirstname} from waitlist`}
              >
                <IconButton
                  color="warning"
                  onClick={() => {
                    leaveWaitlist(registration.staff.mail);
                  }}
                >
                  <PersonRemoveIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </>
      )}
    </Card>
  );
};

export default RegListCard;
